import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Plus, Edit, Trash2, Key } from 'lucide-react';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    password: '',
    roleId: '',
    ownerCodes: ''
  });

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.users);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get('/users/roles');
      setRoles(response.data.roles);
    } catch (err) {
      console.error('Failed to fetch roles:', err);
    }
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({ email: '', fullName: '', password: '', roleId: '', ownerCodes: '' });
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      fullName: user.fullName,
      password: '',
      roleId: user.role?.roleId || '',
      ownerCodes: user.ownerCodes?.join(', ') || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        email: formData.email,
        fullName: formData.fullName,
        roleId: parseInt(formData.roleId),
        ownerCodes: formData.ownerCodes.split(',').map(c => c.trim()).filter(Boolean)
      };

      if (editingUser) {
        await api.put(`/users/${editingUser.user_id}`, payload);
      } else {
        await api.post('/users', { ...payload, password: formData.password });
      }

      setShowModal(false);
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Operation failed');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;
    
    try {
      await api.delete(`/users/${userId}`);
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Delete failed');
    }
  };

  const handleResetPassword = async (userId) => {
    const newPassword = prompt('Enter new password (min 8 characters):');
    if (!newPassword || newPassword.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }

    try {
      await api.post(`/users/${userId}/reset-password`, { newPassword });
      alert('Password reset successfully');
    } catch (err) {
      alert(err.response?.data?.error || 'Reset failed');
    }
  };

  if (loading) {
    return <div className="loading"><div className="spinner" /></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 style={{ fontSize: '1.5rem', fontWeight: '600' }}>Users</h1>
        <button className="btn btn-primary" onClick={openCreateModal}>
          <Plus size={16} /> Add User
        </button>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Owner Codes</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.userId}>
                  <td className="font-medium">{user.fullName}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className="badge badge-info">{user.role?.roleName}</span>
                  </td>
                  <td>{user.ownerCodes?.join(', ') || '-'}</td>
                  <td>
                    <span className={`badge badge-${user.isActive ? 'success' : 'secondary'}`}>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button 
                        className="btn btn-outline btn-sm" 
                        onClick={() => openEditModal(user)}
                        title="Edit"
                      >
                        <Edit size={14} />
                      </button>
                      <button 
                        className="btn btn-outline btn-sm" 
                        onClick={() => handleResetPassword(user.userId)}
                        title="Reset Password"
                      >
                        <Key size={14} />
                      </button>
                      <button 
                        className="btn btn-outline btn-sm" 
                        onClick={() => handleDelete(user.userId)}
                        title="Delete"
                        style={{ color: 'var(--danger)' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '100%', maxWidth: '500px' }}>
            <div className="card-header">
              <h3 className="card-title">{editingUser ? 'Edit User' : 'Create User'}</h3>
              <button className="btn btn-outline btn-sm" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="card-body">
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.fullName}
                    onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    required
                    disabled={!!editingUser}
                  />
                </div>

                {!editingUser && (
                  <div className="form-group">
                    <label className="form-label">Password</label>
                    <input
                      type="password"
                      className="form-input"
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      required
                      minLength={8}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select
                    className="form-input"
                    value={formData.roleId}
                    onChange={(e) => setFormData(prev => ({ ...prev, roleId: e.target.value }))}
                    required
                  >
                    <option value="">Select Role</option>
                    {roles.map((role) => (
                      <option key={role.roleId} value={role.roleId}>{role.roleName}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Owner Codes (comma-separated)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.ownerCodes}
                    onChange={(e) => setFormData(prev => ({ ...prev, ownerCodes: e.target.value }))}
                    placeholder="ABC, DEF, GHI"
                  />
                  <div className="text-sm text-muted" style={{ marginTop: '4px' }}>
                    Assign owner codes for PO visibility
                  </div>
                </div>
              </div>
              <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingUser ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
